import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  Chip,
  Tooltip
} from '@mui/material';
import { Icon } from '@iconify/react';
import { debounceFunc } from 'src/utils/helper';
// hooks
import { useDropdown } from 'src/hooks/useDropdown.hook';

// Session status options
const SESSION_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'INVALID', label: 'Invalid' },
  { value: 'PENDING', label: 'Pending' }
];

// Currency options (common currencies in OCPI)
const CURRENCY_OPTIONS = [
  { value: 'all', label: 'All Currencies' },
  { value: 'INR', label: 'INR (₹)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' }
];

const SessionFilter = ({
  filters = {},
  onFiltersChange,
  onApply,
  onReset,
  loading = false,
  showApplyButton = true,
  showResetButton = true,
  buttonSize = "medium"
}) => {
  // Get dropdown data
  const { data: ocpiPartners } = useDropdown("ocpi_partners");
  const { data: ocpiCommandStatus } = useDropdown("command_status");

  // Debounced search handler
  const debouncedSearch = debounceFunc((value) => {
    onFiltersChange({
      ...filters,
      search: value
    });
  }, 300);

  const handleFilterChange = (field, value) => {
    onFiltersChange({
      ...filters,
      [field]: value
    });
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    debouncedSearch(value);
  };

  const handleDateRangeChange = (field, value) => {
    onFiltersChange({
      ...filters,
      dateRange: {
        ...filters.dateRange,
        [field]: value
      }
    });
  };

  const handleApply = () => {
    if (onApply) {
      onApply();
    }
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  };

  // Count active filters
  const activeFiltersCount = Object.keys(filters).filter(key => {
    const value = filters[key];
    if (key === 'dateRange') {
      return value?.start || value?.end;
    }
    return value && value !== 'all' && value !== 'All Status' && value !== '';
  }).length;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Filter Sessions
          </Typography>
          
          {activeFiltersCount > 0 && (
            <Chip
              label={`${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''} active`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        {/* Main Filter Row */}
        <Grid container spacing={2} alignItems="center">
          {/* Search Field */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Session ID, Location, EVSE..."
              defaultValue={filters.search || ''}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <Icon icon="eva:search-fill" width={20} height={20} />
                  </Box>
                )
              }}
              helperText=" "
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status || 'all'}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              size="small"
              helperText=" "
            >
              {ocpiCommandStatus && ocpiCommandStatus.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Partner Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Partner"
              value={filters.partnerId || 'all'}
              onChange={(e) => handleFilterChange('partnerId', e.target.value)}
              size="small"
              helperText=" "
            >
              <MenuItem value="all">All Partners</MenuItem>
              {ocpiPartners && ocpiPartners.map((partner, index) => (
                <MenuItem key={partner.id || index} value={partner.id || index}>
                  {partner.value || partner.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Currency"
              value={filters.currency || 'all'}
              onChange={(e) => handleFilterChange('currency', e.target.value)}
              size="small"
              helperText=" "
            >
              {CURRENCY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {/* Second Filter Row */}
        <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
          {/* Start Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={filters.dateRange?.start || ''}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={filters.dateRange?.end || ''}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              height: '100%',
              alignItems: 'flex-start',
              pt: 0.5 // Small padding to align with TextField inputs
            }}>
              {showApplyButton && (
                <Button
                  variant="contained"
                  size={buttonSize}
                  onClick={handleApply}
                  disabled={loading}
                  sx={{ flex: 1 }}
                >
                  Apply
                </Button>
              )}
              
              {showResetButton && (
                <Button
                  variant="outlined"
                  size={buttonSize}
                  onClick={handleReset}
                  disabled={loading}
                  sx={{ flex: 1 }}
                >
                  Reset
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Advanced Filters Section */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Advanced Filters
          </Typography>
          
          <Grid container spacing={2}>
            {/* Energy Range */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Min Energy (kWh)"
                value={filters.minEnergy || ''}
                onChange={(e) => handleFilterChange('minEnergy', e.target.value)}
                size="small"
                inputProps={{ min: 0, step: 0.1 }}
                helperText="Minimum energy consumed"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Max Energy (kWh)"
                value={filters.maxEnergy || ''}
                onChange={(e) => handleFilterChange('maxEnergy', e.target.value)}
                size="small"
                inputProps={{ min: 0, step: 0.1 }}
                helperText="Maximum energy consumed"
              />
            </Grid>

            {/* Cost Range */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Min Cost"
                value={filters.minCost || ''}
                onChange={(e) => handleFilterChange('minCost', e.target.value)}
                size="small"
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Minimum session cost"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Max Cost"
                value={filters.maxCost || ''}
                onChange={(e) => handleFilterChange('maxCost', e.target.value)}
                size="small"
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Maximum session cost"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Filter Summary */}
        {activeFiltersCount > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              Active filters: {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} applied
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionFilter;
